/**
 * Simpler version of debounce of lodash and underscore.
 * Invoke a function after sometime delayed. It is like delayed function,
 * but it reset the time if you call again and call after the time elapsed.
 * @param {Function} func
 * @param {Number} wait
 * @param {Boolean} immediate
 * @author: Aécio levy
 * @summary: Insipred by Lodash https://github.com/lodash/lodash/blob/master/debounce.js
 * and https://davidwalsh.name/javascript-debounce-function
 */
function debounce (func, wait, immediate) {
  return Debouncer()(func, wait, immediate);
}

// scope sharing.
function Debouncer () {
  let timeoutID;
  function debounce (func, wait, immediate, force = false) {
    if (typeof func !== 'function') {
      throw new Error('Expected a function');
    }
    function debounced () {
      const lThis = this; const args = [].slice.call(arguments, 0);
      function invokeFunc () {
        timeoutID = null;
        if (!immediate && !force) {
          func.apply(lThis, args);
        }
      }
      const shouldInvokeNow = immediate && !timeoutID;
      // if call again clear the timeout
      clearTimeout(timeoutID);
      // set a timeout reference to the closure
      timeoutID = setTimeout(invokeFunc, wait);
      // if immediate and timeoutID imeddiately invoke the func
      if (shouldInvokeNow || force) {
        func.apply(lThis, args);
      }
    }
    return debounced;
  }
  return debounce;
}

/**
 * Trace to be used with
 * compose, add this between
 * function to get the value
 * carried over.
 * @param {String} label
 */
const trace = label => value => { console.log(`${label}: ${value}`); return value; };

/**
 * Compose functions calls
 * @param  {Function} fns
 */
const compose = (...fns) => x => fns.reduce((acc, curr) => curr(acc), x);

// ----------------
// Axes translators
// ----------------

const ORDERED_AXIS_COLORS = ['green', 'yellow', 'red', 'blue'];

// "yellow" -> 2
function getAxisNumberFromColor (axisColor) {
  const result = ORDERED_AXIS_COLORS.indexOf(axisColor);
  return result != -1 ? result + 1 : -1;
}

// magic
function getAxisNumber (anyAxisRepr) {
  function getAxisNumberFromLetter (axisCoord) {
    const axis = ['x', 'y', 'z', 'w'].indexOf(axisCoord.toLowerCase());
    return axis != -1 ? axis + 1 : -1;
  }

  if (typeof anyAxisRepr === 'string' && /^[1234]$/.test(anyAxisRepr)) { anyAxisRepr = parseInt(anyAxisRepr, 10); }

  if (typeof anyAxisRepr === 'number') {
    if (anyAxisRepr >= 1 && anyAxisRepr <= 4) {
      return anyAxisRepr;
    }
    else {
      console.error('axis must be between 1 and 4');
    }
  }
  else if (typeof anyAxisRepr === 'string') {
    if (['x', 'y', 'z', 'w'].includes(anyAxisRepr.toLowerCase())) {
      return getAxisNumberFromLetter(anyAxisRepr);
    }
    else if (ORDERED_AXIS_COLORS.includes(anyAxisRepr)) {
      return getAxisNumberFromColor(anyAxisRepr);
    }
    else {
      console.error(`axis must be one of "x", "y", "z", "w", ${ORDERED_AXIS_COLORS.map(axisColorStr => `"${axisColorStr}"`).join(', ')}`);
    }
  }
  else {
    console.error('axis must be a string or a number');
    console.log(anyAxisRepr);
  }
}

// return an object with methods to convert the given axis representation to the corresponding axis representation.
// conversion is lazy
function convert (anyAxisRepr) {
  function getAxisColorFromNumber (axis) {
    return axis >= 1 && axis <= 4 ? ORDERED_AXIS_COLORS[axis - 1] : -1;
  }

  function getAxisLetterFromNumber (axis) {
    if (axis === 1) { return 'x'; }
    else if (axis === 2) { return 'y'; }
    else if (axis === 3) { return 'z'; }
    else if (axis === 4) { return 'w'; }
    else { return 'Axis Error'; }
  }

  const axisNumber = getAxisNumber(anyAxisRepr);

  return {
    toColor: () => getAxisColorFromNumber(axisNumber),
    toNumber: () => axisNumber,
    toLetter: () => getAxisLetterFromNumber(axisNumber),
  };
}

// -------------------
// Others
// -------------------

function formatAxisType (currentValue) {
  switch (currentValue) {
    case 'belt' : return 'Timing Belt';
    case 'enclosed_timing_belt' : return 'Enclosed Timing Belt';
    case 'rack_and_pinion' : return 'Rack & Pinion';
    case 'rack_and_pinion_v2' : return 'Rack & Pinion v2';
    case 'ball_screw' : return 'Ball Screw';
    case 'enclosed_ball_screw' : return 'Enclosed Ball Screw';
    case 'enclosed_lead_screw' : return 'Enclosed Lead Screw';
    case 'indexer' : return 'Rotary Actuator v1';
    case 'indexer_v2' : return 'Rotary Actuator v2';
    case 'electric_cylinder' : return 'Electric Cylinder';
    case 'belt_conveyor' : return 'Belt Conveyor';
    case 'roller_conveyor' : return 'Roller Conveyor';
    case 'pneumatic' : return 'Pneumatic';
    case 'pneumatic_bag_gripper' : return 'Pneumatic Bag Gripper';
    case 'belt_rack' : return 'Belt Rack';
    case 'heavy_duty_roller_conveyor' : return 'Heavy-Duty Roller Conveyor';
    case 'telescopic_column' : return 'Telescopic Column';
    case 'timing_belt_conveyor' : return 'Timing Belt Conveyor';
    case 'custom' : return 'Custom';
  }
}

function getAxisIconFromType (axisType) {
  if (['belt', 'enclosed_timing_belt'].includes(axisType)) {
    return 'icon-belt-actuator';
  }
  else if (['rack_and_pinion', 'rack_and_pinion_v2', 'belt_rack'].includes(axisType)) {
    return 'icon-rack-andpinion';
  }
  else if (['ball_screw', 'enclosed_ball_screw', 'enclosed_lead_screw', 'telescopic_column'].includes(axisType)) {
    return 'icon-ballscrew';
  }
  else if (['indexer', 'indexer_v2'].includes(axisType)) {
    return 'icon-rotary-indexer';
  }
  else if (['electric_cylinder'].includes(axisType)) {
    return 'icon-electric-actuator';
  }
  else if (['belt_conveyor', 'roller_conveyor', 'heavy_duty_roller_conveyor', 'timing_belt_conveyor'].includes(axisType)) {
    return 'icon-conveyor';
  }
  else if (['pneumatic', 'pneumatic_bag_gripper'].includes(axisType)) {
    return 'icon-pneumatic'; // TODO : verify that it is the right icon
  }
  else {
    return 'icon-axis';
  }
}

const formatFloat = (number, precision = 3) => {
  const multiple = Math.pow(10, precision);
  return Math.floor(parseFloat(number) * multiple) / multiple;
};

export default {
  ORDERED_AXIS_COLORS,
  compose,
  debounce,
  trace,
  Debouncer,
  getAxisNumberFromColor,
  convert,
  formatAxisType,
  getAxisIconFromType,
  formatFloat,
};
